import { BAM_PLAYER_FIT } from './constants';
import staticCss from './bamPlayer.scss';

export const getStyleSheetForBamPlayer = ({
  aspectRatio,
  fontSize,
  showOnlyThumbnails,
  poster,
  posterLowResBlob,
  playerFit,
  showPosterWhenPaused,
  theme,
  isInlinePlayer,
}) => {
  let effectiveHeight;
  let effectiveWidth;

  switch (playerFit) {
    case BAM_PLAYER_FIT.FILL_PARENT:
      effectiveWidth = `100%`;
      effectiveHeight = `100%`;
      break;
    case BAM_PLAYER_FIT.EXACT_SIZE:
      effectiveWidth = `var(--bam-player-width, var(--internal-width, 250px))`;
      effectiveHeight = `var(--bam-player-height, var(--internal-height, 444px))`;
      break;
    case BAM_PLAYER_FIT.SAME_WIDTH:
      // same width, different height
      effectiveWidth = `var(--bam-player-width, var(--internal-width, 250px))`;
      effectiveHeight = `calc(${effectiveWidth} / ${aspectRatio})`;
      break;
    case BAM_PLAYER_FIT.SAME_HEIGHT:
    default:
      // same height, different width
      // default option
      effectiveHeight = `var(--bam-player-height, var(--internal-height, 444px))`;
      effectiveWidth = `calc(${effectiveHeight} * ${aspectRatio})`;
      break;
  }

  const borderRadiusBase = theme?.generalV2?.playerSettings?.misc?.borderRadiusBase;
  const productImagePadding = theme?.generalV2?.playerSettings?.productImage?.productImagePadding ?? 0;

  const actionCards = theme?.generalV2?.playerSettings?.actionCards;
  let actionCardsStyles = '';
  if (actionCards) {
    actionCardsStyles += `  --internal-action-card-backgroundColor: ${actionCards.backgroundColor};\n`;
    actionCardsStyles += `  --internal-action-card-foregroundColor: ${actionCards.foregroundColor};\n`;
    actionCardsStyles += `  --internal-action-card-borderColor: ${actionCards.borderColor};\n`;
    actionCardsStyles += `  --internal-action-card-borderWidth: ${actionCards.borderWidth};\n`;
    actionCardsStyles += `  --internal-action-card-borderRadius: ${actionCards.borderRadius};\n`;
  }

  let colorValues = '';
  if (theme?.generalV2?.colors) {
    Object.entries({
      ...theme?.generalV2?.colors.main,
      ...theme?.generalV2?.colors.other
    }).forEach(([propertyKey, propertyValue]) => {
      colorValues += `  --${propertyKey}: ${propertyValue};\n`;
    });
  }

  /* eslint-disable */
  const dynamicCss = `
    :host {
        display: ${(isInlinePlayer || aspectRatio !== undefined) ? 'block' : 'none'};
        max-width: ${effectiveWidth};
        position: relative;
        aspect-ratio: ${aspectRatio};
        font-size: ${fontSize}px;
        overflow: hidden;
        border-radius: var(--bam-player-corner-radius, var(--internal-corner-radius, 0px));
        width: ${effectiveWidth};

        ${isInlinePlayer ? `
          min-width: 320px;
          min-height: 320px;
        ` : ''}

        --effective-height: ${effectiveHeight};
        height: var(--effective-height);

        --title-10-font-size: var(--bam-player-title-10-font-size, var(--internal-overlay-title-font-size, 22px));
        --title-10-line-height: var(--bam-player-title-10-line-height, var(--internal-overlay-title-line-height, 1));

        --title-20-font-size: var(--bam-player-title-20-font-size, 16px);
        --title-20-line-height: var(--bam-player-title-20-line-height, 1);

        --subtitle-20-font-size: var(--bam-player-subtitle-20-font-size, 14px);
        --subtitle-20-line-height: var(--bam-player-subtitle-20-line-height, 1);

        --border-radius-base: ${borderRadiusBase ? `${borderRadiusBase}` : '4px'};
        --border-radius-medium: calc(var(--border-radius-base, 4px) * 1.5);
        --border-radius-large: calc(var(--border-radius-base, 4px) * 3);

        --product-image-padding: ${productImagePadding};

        ${colorValues}

        ${actionCardsStyles}

        --action-card-background-color: var(--bam-player-action-card-background-color, var(--internal-action-card-backgroundColor, rgba(41, 41, 41, 0.8)));
        --action-card-foreground-color: var(--bam-player-action-card-foreground-color, var(--internal-action-card-foregroundColor, #FFF));

        --action-card-border-color: var(--bam-player-action-card-border-color, var(--internal-action-card-borderColor, var(--color-inverted-subdued, rgba(117, 117, 117, 1))));
        --action-card-border-radius: var(--bam-player-action-card-corner-radius, var(--internal-action-card-borderRadius, var(--border-radius-large)));
        --action-card-border-width: var(--bam-player-action-card-border-width, var(--internal-action-card-borderWidth, 1px));

        --overlay-elements-width-base: var(--bam-player-overlay-elements-width-base, 360px);
        --thumbnail-size: var(--effective-thumbnail-size, 68px);
        --thumbnail-padding: var(--bam-player-product-image-padding, var(--product-image-padding));
        --product-card-padding: var(--bam-player-product-card-padding, var(--internal-product-card-padding, min(0.6em, 6px)));

        --closed-captions-color: var(--bam-player-closed-captions-color, #FFF);
        --closed-captions-background-color: var(--bam-player-closed-captions-background-color, rgba(0, 0, 0, 0.6));
        --closed-captions-padding: var(--bam-player-closed-captions-padding, 8px);
        --closed-captions-margin: var(--bam-player-closed-captions-margin, 0 8px);
        --closed-captions-text-align: var(--bam-player-closed-captions-text-align, start);
    }
    .placeholder-low-res {
        ${
          posterLowResBlob
            ? `
            display: block;
            background-color: var(--bam-player-placeholder-color, var(--internal-placeholder-color, #EEE));
            background-image: url(${posterLowResBlob});
            background-size: cover;
            background-position: center;
            `
            : `
            display: none;
            `
        }
    }
    .placeholder {
        ${
          !poster
            ? ``
            : `
            background-image: url(${poster});
            background-size: cover;
            background-position: center;
            `
        }
    }
    .overlay .products:has(li:nth-child(2)) li {
        ${showOnlyThumbnails ? '' : 'flex-shrink: 0;'}
    }
    .overlay .products .product .product-info {
        color: var(--bam-player-product-text-color, #000);
        ${showOnlyThumbnails ? 'display: none;' : ''}
    }
    ${
      ((poster || posterLowResBlob) && showPosterWhenPaused) || isInlinePlayer ?
      `
        .wrapper.is-playing .placeholder,
        .wrapper.is-playing .placeholder-low-res {
            opacity: 0;
        }
        `: ''
    }
    ${
      ((poster || posterLowResBlob) && !showPosterWhenPaused) || isInlinePlayer ?
      `
        .wrapper.played-at-least-once .placeholder,
        .wrapper.played-at-least-once .placeholder-low-res {
            opacity: 0;
        }
        `: ''
    }
    `;

  return staticCss + dynamicCss;
};
/* eslint-enable */
